import { MAX_TOPICS_PER_POST } from "@circle-react/components/QuickPostV2/Content/TopicsPicker/constants";
import { useCreateTopic } from "@circle-react/components/QuickPostV2/Content/TopicsPicker/hooks/useCreateTopic";
import { useTopics } from "@circle-react/components/QuickPostV2/Content/TopicsPicker/hooks/useTopics";
import { useTopicsPermissions } from "@circle-react/components/QuickPostV2/Content/TopicsPicker/hooks/useTopicsPermissions";
import { useDebouncedValue } from "@circle-react/hooks/utils/useDebouncedValue";
import type { Topic } from "@circle-react/types/Topic";
import type { SelectOption } from "@circleco/compass/components/MultiSelect";
import { keyBy } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { keepPreviousData } from "react-query";

const topicToSelectOption = (topic: Topic): SelectOption<number> => ({
  value: topic.id,
  label: topic.name,
});

interface UseTopicsMultiSelectOptions {
  creatable?: boolean;
  maxSelections?: number;
  onChange: (ids: number[]) => void;
  spaceId?: string;
  value: number[];
}

export const useTopicsMultiSelect = ({
  spaceId,
  value: selectedIds,
  onChange,
  creatable = false,
  maxSelections = MAX_TOPICS_PER_POST,
}: UseTopicsMultiSelectOptions) => {
  const [query, setQuery] = useState("");
  const [createdTopicById, setCreatedTopicById] = useState<
    Record<number, Topic>
  >({});
  const trimmedQuery = query.trim();
  const debouncedQuery = useDebouncedValue(trimmedQuery, 200);
  const hasQuery = trimmedQuery.length > 0;
  const hasDebouncedQuery = debouncedQuery.length > 0;

  const { canSelectTopic, canCreateTopics } = useTopicsPermissions({
    creatable,
  });

  const { data: spaceTopicsData, isPending: isLoadingSpaceTopics } = useTopics({
    spaceId,
    queryOptions: { enabled: !!spaceId },
  });

  const { data: topicsData, isPending: isLoadingSearch } = useTopics({
    spaceId,
    query: debouncedQuery,
    queryOptions: {
      enabled: !!spaceId && hasDebouncedQuery,
      placeholderData: keepPreviousData,
    },
  });

  const { data: selectedTopicsData, isFetching: isFetchingSelectedTopics } =
    useTopics({
      spaceId,
      ids: selectedIds,
      queryOptions: {
        enabled: !!spaceId && selectedIds.length > 0,
        placeholderData: keepPreviousData,
      },
    });

  const validSelectedIds = useMemo(() => {
    if (isFetchingSelectedTopics || !selectedTopicsData) return selectedIds;

    return selectedIds.filter(selectedId =>
      selectedTopicsData.records.some(topic => topic.id === selectedId),
    );
  }, [isFetchingSelectedTopics, selectedIds, selectedTopicsData]);

  useEffect(() => {
    if (validSelectedIds.length !== selectedIds.length) {
      onChange(validSelectedIds);
    }
  }, [onChange, selectedIds.length, validSelectedIds]);

  const optionRecords = useMemo(
    () =>
      hasDebouncedQuery
        ? (topicsData?.records ?? [])
        : (spaceTopicsData?.records ?? []),
    [hasDebouncedQuery, spaceTopicsData?.records, topicsData?.records],
  );

  const { mutate: createTopic, isPending: isCreating } = useCreateTopic({
    spaceId,
    onSuccess: (topic: Topic) => {
      setCreatedTopicById(previous => ({ ...previous, [topic.id]: topic }));
      setQuery("");
      if (!selectedIds.includes(topic.id)) {
        onChange([...selectedIds, topic.id]);
      }
    },
  });

  const topicById = useMemo(() => {
    const topics = [
      ...(spaceTopicsData?.records ?? []),
      ...(selectedTopicsData?.records ?? []),
      ...(topicsData?.records ?? []),
      ...Object.values(createdTopicById),
    ];

    return keyBy(topics, "id");
  }, [
    createdTopicById,
    selectedTopicsData?.records,
    spaceTopicsData?.records,
    topicsData?.records,
  ]);

  const dropdownOptions = useMemo(
    () =>
      optionRecords
        .filter(
          topic => canSelectTopic(topic) && !selectedIds.includes(topic.id),
        )
        .map(topicToSelectOption),
    [optionRecords, selectedIds, canSelectTopic],
  );

  const selectedOptions = useMemo(
    () =>
      validSelectedIds.map(id => {
        const topic = topicById[id];
        return topic
          ? topicToSelectOption(topic)
          : { value: id, label: String(id) };
      }),
    [topicById, validSelectedIds],
  );

  const hasSelectableTopicsInSpace = useMemo(
    () => (spaceTopicsData?.records ?? []).some(canSelectTopic),
    [spaceTopicsData, canSelectTopic],
  );

  const hasMaxTopicsSelected = validSelectedIds.length >= maxSelections;
  const canCreateOption =
    canCreateTopics && !!spaceId && !hasMaxTopicsSelected && hasQuery;
  const isWaitingForSearch = hasQuery && trimmedQuery !== debouncedQuery;
  const shouldOpenDropdown =
    isWaitingForSearch ||
    isLoadingSearch ||
    dropdownOptions.length > 0 ||
    canCreateOption;

  const shouldShow =
    isLoadingSpaceTopics ||
    hasSelectableTopicsInSpace ||
    canCreateTopics ||
    validSelectedIds.length > 0;
  const isLoadingDropdownOptions =
    (isWaitingForSearch || isLoadingSearch || isCreating) &&
    dropdownOptions.length === 0;

  const handleValueChange = useCallback(
    (options: SelectOption<number>[]) => {
      onChange(options.map(option => option.value));
    },
    [onChange],
  );

  return {
    createTopic,
    creatable: canCreateTopics && !!spaceId && !hasMaxTopicsSelected,
    dropdownOptions,
    handleValueChange,
    hasMaxTopicsSelected,
    isLoading: isLoadingDropdownOptions,
    onInputValueChange: setQuery,
    selectedOptions,
    shouldOpenDropdown,
    shouldShow,
  };
};
